<script lang="ts" setup>
import { useSlots, PropType, computed, ref } from 'vue';
import { useManagedListBlock } from 'ah-common-lib/src/listing/useManagedListBlock';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import {
  UseManagedListBlockEmits,
  defineUseManagedListBlockProps,
} from 'ah-common-lib/src/listing/useManagedListBlockInterfaces';
import { ListingTriggers } from 'ah-common-lib/src/common/components/listing/Listing.vue';
import ListPageCounter from './ListPageCounter.vue';
import ListPagination from './ListPagination.vue';
import MaterialIcon from '../common/MaterialIcon.vue';
import { isEmpty, isEqual } from 'lodash';
import { VButton } from 'ah-common-lib/src/common/components';
import ListDownloadButton from 'ah-common-lib/src/common/components/listing/ListDownloadButton.vue';

const slots = useSlots();

/**
 * Base List block component
 *
 * Meant for usage alongside a <Listing> and a <fooFilter> components, controlling filter flow between both
 * All data is synchronized via slot scopes to Listing and Filter components
 *
 * This component does NOT save filter/pagination data. Any query-string-based data saving should happen on the child components
 * (or, as is common, on the Filter components, which MAY ALSO save pagination data)
 */

const props = defineProps({
  ...defineUseManagedListBlockProps(),

  showExport: { type: [Boolean, String], default: false },

  showColConfigurator: { type: [Boolean, String], default: false },

  showSizeSelector: { type: [Boolean, String], default: true },

  downloadFormatOptions: {
    type: Array as PropType<{ value: string; text: string }[]>,
    default: () => [
      { value: 'PDF', text: 'PDF' },
      { value: 'XLSX', text: 'Excel' },
      { value: 'CSV', text: 'CSV' },
    ],
  },

  pageOptions: {
    type: Array as PropType<{ label: string; value: number }[]>,
    default: () => [
      { label: '10', value: 10 },
      { label: '20', value: 20 },
      { label: '50', value: 50 },
      { label: '100', value: 100 },
    ],
  },
});

interface AHPanelListBlockEmits extends UseManagedListBlockEmits {}

const emit = defineEmits<AHPanelListBlockEmits>();

const requestManager = useRequestManager().manager;

const filtersShown = ref(false);

const isSearchBannerShown = computed(() => managedListBlock.refs.filter.value?.hasOwnProperty('query'));

const managedListBlock = useManagedListBlock({
  reqManager: requestManager,
  props,
  emit,
});

const listingTriggers = ref<ListingTriggers<any>>();

const filterSlotScope = computed(() => managedListBlock.filterSlotScope.value);

/**
 * Slot scope for the list slot. All necessary hooks are exposed, grouped into `listeners` and `props` for a less verbose integration
 *
 * Listeners for filter and sortAndPageParams for listings are SYNC by default (as opposed to the filter listeners, which update on a debounce)
 */
const listSlotScope = computed(() => {
  return {
    ...managedListBlock.listSlotScope.value,
    listeners: {
      ...managedListBlock.listSlotScope.value.listeners,
      ['update:triggers']: onListTriggersUpdate,
    },
    props: managedListBlock.listSlotScope.value.props,
    onListTriggersUpdate: onListTriggersUpdate,
    downloadData: downloadData,
    loadData: loadData,
    hasCustomFilters,
  };
});

function onListTriggersUpdate(triggers: ListingTriggers<any>) {
  listingTriggers.value = triggers;
}

function loadData() {
  listingTriggers.value?.loadData();
}

function downloadData(type: string) {
  listingTriggers.value?.downloadData(type);
}

const hasCustomFilters = computed(() => {
  return (
    managedListBlock.refs.filter.value &&
    !isEmpty(managedListBlock.refs.filter.value) &&
    !isEqual(managedListBlock.refs.commonFilter.value, managedListBlock.refs.filter.value)
  );
});

const footerSlotScope = computed(() => {
  return {
    setPage: managedListBlock.setPage,
    setPageSize: managedListBlock.setPageSize,
    data: managedListBlock.refs.data,
    dataDownloadState: managedListBlock.refs.dataDownloadState,
    downloadData: downloadData,
    loadData: loadData,
  };
});

const dataDownloadState = computed(() => managedListBlock.refs.dataDownloadState.value);

defineExpose({
  managedListBlock,
});
</script>
<template>
  <div class="card">
    <div class="d-flex">
      <slot name="card-top" v-bind="filterSlotScope" />
      <div class="ml-auto">
        <div class="card-body table-header">
          <div class="header-actions">
            <div class="header-actions-inner">
              <template v-if="slots.filter">
                <button
                  class="btn btn-ghost clear-filters"
                  :disabled="!hasCustomFilters"
                  @click="managedListBlock.setFilter({})"
                  v-show="filtersShown || hasCustomFilters"
                >
                  Clear All Filters
                </button>
                <button class="btn btn-secondary show-filters" @click="filtersShown = !filtersShown">
                  <MaterialIcon :icon="filtersShown ? 'filter_alt_off' : 'filter_alt'" />
                  {{ filtersShown ? 'Hide' : 'Show' }} Filters
                </button>
              </template>
              <div class="search-bar">
                <slot name="search" v-bind="filterSlotScope"> </slot>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="card-body search-box" v-if="isSearchBannerShown">
      <div class="search-results-banner d-flex">
        <div class="search-results">
          <h4 class="mt-1">
            {{ managedListBlock.refs.data.value?.total }} <span class="search-results-text">search result(s)</span>
          </h4>
        </div>
        <div>
          <VButton class="btn-ghost" @click="managedListBlock.setFilter({})">Clear My Search</VButton>
        </div>
      </div>
    </div>
    <div class="filter-box" v-show="filtersShown" v-if="slots.filter">
      <slot name="filter" v-bind="filterSlotScope" />
    </div>
    <slot name="list" v-bind="listSlotScope" />
    <div class="card-body table-footer">
      <div class="footer-actions">
        <div class="row-count">
          <slot name="rowCount" v-bind="footerSlotScope">
            <div class="row-count-inner">
              <ListPageCounter
                v-if="managedListBlock.refs.data.value && managedListBlock.refs.data.value.total > 0"
                :data="managedListBlock.refs.data.value"
              />
            </div>
          </slot>
        </div>
        <div class="pagination">
          <slot name="pagination" v-bind="footerSlotScope">
            <ListPagination
              class="pagination-inner"
              v-if="
                managedListBlock.refs.data.value &&
                managedListBlock.refs.data.value.total > (managedListBlock.refs.data.value.pageSize || 10)
              "
              :data="managedListBlock.refs.data.value"
              @set-page="managedListBlock.setPage"
            />
          </slot>
        </div>
        <div class="export">
          <slot name="export" v-bind="footerSlotScope">
            <div class="export-inner">
              <ListDownloadButton
                v-if="showExport !== false"
                :loading="dataDownloadState === 'pending'"
                class="download-button"
                @download="downloadData"
                :downloadOptions="downloadFormatOptions"
                buttonClass="btn-secondary export-button"
              >
                <template #download-button-content> <MaterialIcon icon="download"></MaterialIcon> Export</template>
              </ListDownloadButton>
            </div>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.card {
  box-shadow: none;

  .header-actions-inner {
    display: flex;
    justify-content: flex-end;
  }

  .search-bar {
    width: 300px;
  }

  .filter-box {
    padding: 15px 20px;
    margin-bottom: 20px;
    box-shadow: 0px 0px 10px 0px rgba(158, 171, 203, 0.6);
    min-height: 4rem;
  }

  .search-results-banner {
    width: 100%;
    @include themedBackgroundColor($color-bdLightGrey);
    @include themedTextColor($color-bdDarkBlue);
    display: flex;
    justify-content: space-between;
    border: 1px solid getColor($color-bdMediumGrey);
    border-radius: 5px;
    font-weight: 600;
    font-size: $font-size-base;
    padding: 6px 12px;

    .search-results {
      font-size: $h4-font-size;

      .search-results-text {
        font-weight: 400;
      }
    }
  }

  .footer-actions {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;

    .row-count {
      display: flex;
      height: 100%;
      align-items: flex-end;
      justify-content: flex-start;
    }

    .pagination-inner {
      display: flex;
      justify-content: center;
      height: 30px;
    }

    .export-inner {
      display: flex;
      height: 100%;
      align-items: flex-end;
      justify-content: flex-end;
    }
  }
}
.clear-filters {
  margin-right: 20px;
}

.show-filters {
  margin-right: 23px;
}

.search-box {
  padding-top: 0;
}

::v-deep {
  .export-button {
    display: flex;
    align-items: center;
    padding-left: 10px !important;
    height: 30px;
  }

  .select-input {
    display: flex;
    margin-right: 10px !important;
    padding: 0 33px 0 12px;
    height: 30px;
  }

  .dropdown-icon {
    margin-right: 10px !important;
    bottom: 0.2em !important;
  }

  .download-button {
    display: flex;
    align-content: center;
    align-items: center;
  }

  .table-footer {
    padding-bottom: 36px;
  }
}
</style>
